import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #undoLine = null;
  #undoBusy = false;
  #cartRevision = 0;
  #undoMessage = '';
  #layoutFrame = null;
  #resizeObserver = new ResizeObserver(() => this.#scheduleLayout());

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleUndoClick);
    this.addEventListener('keydown', this.#handleTabBoundary);
    if (this.#dialog) this.#resizeObserver.observe(this.#dialog);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    window.addEventListener('resize', this.#scheduleLayout);
    window.visualViewport?.addEventListener('resize', this.#scheduleLayout);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleUndoClick);
    this.removeEventListener('keydown', this.#handleTabBoundary);
    this.#resizeObserver.disconnect();
    window.removeEventListener('resize', this.#scheduleLayout);
    window.visualViewport?.removeEventListener('resize', this.#scheduleLayout);
    if (this.#layoutFrame !== null) cancelAnimationFrame(this.#layoutFrame);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#scheduleLayout();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    this.#captureUndo(event);
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => this.#scheduleLayout();

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #handleTabBoundary = (event) => {
    if (event.key !== 'Tab' || !this.#themeDrawer?.hasAttribute('force-modal') || !this.#dialog?.open) return;
    if (this.querySelector('dialog[open]')) return;
    const focusable = [...this.#dialog.querySelectorAll('button:not(:disabled), a[href], input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, iframe, [tabindex]:not([tabindex="-1"])')].filter((node) => {
      if (!node.getClientRects().length || node.closest('[hidden], [inert], dialog:not([open])') || getComputedStyle(node).visibility === 'hidden') return false;
      let closedDetails = node.closest('details:not([open])');
      while (closedDetails) {
        if (node !== closedDetails.querySelector(':scope > summary')) return false;
        closedDetails = closedDetails.parentElement?.closest('details:not([open])');
      }
      return true;
    });
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    }
  };

  #captureUndo(event) {
    const revision = ++this.#cartRevision;
    let removed = null;
    if (event.action === 'remove' && event.target instanceof Node && this.contains(event.target)) {
      const lineId = event.lines?.[0]?.id;
      const row = [...this.querySelectorAll('[data-restore-id]')].find((row) => row.dataset.key === lineId);
      if (row) {
        try {
          const properties = JSON.parse(row.dataset.restoreProperties || '{}');
          removed = {
            id: Number(row.dataset.restoreId),
            quantity: Number(row.dataset.restoreQuantity),
            properties: Array.isArray(properties) ? Object.fromEntries(properties) : properties,
          };
          if (row.dataset.restorePlan) removed.selling_plan = Number(row.dataset.restorePlan);
        } catch {
          removed = null;
        }
      }
    }
    this.#undoLine = null;
    this.#undoMessage = '';
    this.#renderUndo();
    event.promise?.then(({ detail }) => {
      if (revision !== this.#cartRevision || detail?.didError) return;
      this.#undoLine = removed;
      requestAnimationFrame(() => this.#renderUndo());
    }).catch(() => {});
  }

  #renderUndo() {
    this.querySelector('.ihh-cart-undo')?.remove();
    if (!this.#undoLine && !this.#undoMessage) return;
    const target = this.querySelector('.cart-drawer__summary') || this.querySelector('.cart-drawer__content');
    if (!target) return;
    const notice = document.createElement('div');
    notice.className = 'ihh-cart-undo';
    notice.setAttribute('role', 'status');
    const text = document.createElement('span');
    text.textContent = this.#undoMessage || this.dataset.removedLabel;
    notice.append(text);
    if (this.#undoLine) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.cartUndo = '';
      button.textContent = this.dataset.undoLabel;
      button.disabled = this.#undoBusy;
      notice.append(button);
    }
    target.prepend(notice);
    this.#updateStickyState();
  }

  #handleUndoClick = async (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-cart-undo]')) return;
    if (!this.#undoLine || this.#undoBusy) return;
    const item = this.#undoLine;
    this.#undoBusy = true;
    this.#renderUndo();
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((node) => node.dataset.sectionId))];
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart',
      lines: [{ merchandiseId: String(item.id), quantity: item.quantity }],
      promise: deferred.promise,
    }));
    let rejected = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
        body: JSON.stringify({ items: [item], sections: sectionIds, sections_url: location.pathname }),
      }));
      const added = await response.json();
      if (!response.ok || added.status) {
        rejected = true;
        throw new Error(added.description || added.message || this.dataset.restoreError);
      }
      const cartResponse = await fetch(Theme.routes.cart_url + '.js');
      if (!cartResponse.ok) throw new Error(this.dataset.restoreUncertain);
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: added.sections, items: cart.items, itemCount: cart.item_count, source: 'cart-drawer-undo', didError: false },
      });
      this.#undoLine = null;
      this.#undoMessage = '';
      requestAnimationFrame(() => {
        this.#renderUndo();
        const restored = [...this.querySelectorAll('[data-restore-id]')].find((row) => Number(row.dataset.restoreId) === item.id);
        restored?.querySelector('.cart-items__title')?.focus({ preventScroll: true });
      });
    } catch (error) {
      deferred.reject(error);
      this.#undoLine = rejected ? item : null;
      this.#undoMessage = rejected ? error.message : this.dataset.restoreUncertain;
      if (!rejected) await sectionRenderer.renderSection('cart-drawer-section', { cache: false, mode: 'hydration' }).catch(() => {});
    } finally {
      this.#undoBusy = false;
      requestAnimationFrame(() => this.#renderUndo());
    }
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #scheduleLayout = () => {
    if (this.#layoutFrame !== null) cancelAnimationFrame(this.#layoutFrame);
    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = null;
      if (!this.isConnected) return;
      const drawer = this.#themeDrawer;
      if (drawer?.classList.contains('ihh-cart-drawer')) {
        const viewport = window.visualViewport?.height || window.innerHeight;
        drawer.style.setProperty('--ihh-cart-viewport', `${Math.round(viewport)}px`);
      }
      this.#updateStickyState();
    });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog?.open) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
